import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { RangeFilter } from "@/components/ui/RangeFilter";
import { PageHeader, TableShell, Th } from "@/features/power_projects/components/ui";
import { fmtDate, fmtDateTime } from "@/features/power_projects/utils";
import { useAuthStore } from "@/store/auth";
import { ACTION_STATUS_COLORS, tenderActionLabel, type TenderActionStatus } from "@/features/tenders/types";
import { useZETenders, useZETenderActionCounts } from "../hooks";
import { useTenderListLive } from "@/features/tenders/hooks/useTenderListLive";
import { ZETenderCreateDialog } from "../components/ZETenderCreateDialog";
import { EXCLUDABLE_REJONY, isRejonExcluded } from "../rejonExclusion";
import type { ZETender } from "../types";

const STATUS_LABEL: Record<string, string> = {
  open: "Otwarte",
  won_unofficial: "Wygrany - nieoficjalnie",
  won: "Wygrane",
  lost: "Przegrane",
  cancelled: "Anulowane",
  not_starting: "Nie startujemy",
};

// Status filter offers the full canonical set (mirrors the main Przetargi page) so
// e.g. "Wygrany - nieoficjalnie" is always selectable — not only once a ZE tender
// already has that status (the other filters stay data-derived).
const STATUS_FILTER_ORDER = [
  "open",
  "won_unofficial",
  "won",
  "lost",
  "cancelled",
  "not_starting",
];

const SCOPE_LABEL: Record<string, string> = {
  projekt: "Projekt",
  wykonawstwo: "Wykonawstwo",
  projekt_i_wykonawstwo: "Projekt i wykonawstwo",
};

const scopeLabel = (s: string | null) => (s ? (SCOPE_LABEL[s] ?? s) : "—");

/** Zakres, który liczy się operacyjnie: pole twarde z prefiksu nazwy, a dopiero w jego
 *  braku wnioskowanie AI. "" = nieustalony i wymaga człowieka — nie zgadujemy. */
const effectiveScope = (t: ZETender) => t.work_scope || t.contract_scope || "";

/** Prefiks mówi co innego niż analiza AI — obie strony mają zdanie i się różnią. */
const scopeConflict = (t: ZETender) =>
  !!t.work_scope && !!t.contract_scope && t.work_scope !== t.contract_scope;

const PAGE_SIZE = 40;

/** Długość trasy użyta w sortowaniu i w filtrze zakresu — przyłącze (short_connection)
 *  nie ma narysowanej trasy, ale znaczy „krótkie", więc liczy się jako 0 m. Brak danych
 *  zostaje `null`: nieznanej długości nie da się wstawić w przedział. */
const effectiveLengthM = (t: ZETender): number | null =>
  t.cable_length_m ?? (t.project_type === "short_connection" ? 0 : null);

/** PLN amount from a decimal string → "123 456,78 zł". */
const fmtPLN = (v: string) => {
  const n = Number(v);
  return Number.isFinite(n)
    ? n.toLocaleString("pl-PL", { style: "currency", currency: "PLN", useGrouping: "always" })
    : v;
};

// Action chips that only make sense before the offer deadline — selecting one turns on
// "tylko przed terminem"; needs_result is post-deadline by definition so it turns it off.
const PRE_DEADLINE_CHIPS = new Set<NonNullable<TenderActionStatus>>([
  "needs_pricing", "pricing_claimed", "needs_approval", "needs_offer",
]);

// ── sortable column header ────────────────────────────────────────────────────
type SortKey =
  | "title" | "client" | "operator" | "network" | "scope"
  | "length" | "deadline" | "status" | "variants" | "submitted" | "submittedAt";

interface SortState {
  key: SortKey | null;
  dir: "asc" | "desc";
}

function SortHeader({
  label,
  col,
  sort,
  onSort,
  className,
}: {
  label: string;
  col: SortKey;
  sort: SortState;
  onSort: (k: SortKey) => void;
  className?: string;
}) {
  const active = sort.key === col;
  return (
    <Th className={className}>
      <button
        type="button"
        onClick={() => onSort(col)}
        className="inline-flex items-center gap-1 uppercase tracking-widest hover:text-[var(--color-text-primary)] transition-colors"
        title="Sortuj"
      >
        {label}
        <span
          className={
            active
              ? "text-[var(--color-primary)]"
              : "text-[var(--color-text-muted)] opacity-40"
          }
        >
          {active ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </Th>
  );
}

// ── filter dropdown ───────────────────────────────────────────────────────────
function FilterSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      className="input-base w-auto min-w-[150px]"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

// ── excluded-rejony dropdown (multi-checkbox) ─────────────────────────────────
// Rozwijana lista z checkboxami: zaznaczone Rejony Dystrybucji (RE) są wykluczane
// z listy. Wartości opcji to nazwy miast rejonu (dopasowywane do `energa_rejon`).
function ExcludedRejonyFilter({
  options,
  excluded,
  onToggle,
}: {
  options: string[];
  excluded: string[];
  onToggle: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="input-base w-auto min-w-[150px] text-left inline-flex items-center gap-2 whitespace-nowrap"
      >
        <span className="flex-1">
          Wykluczone rejony (RE)
          {excluded.length > 0 && (
            <span className="ml-1 text-[var(--color-primary)] font-semibold">({excluded.length})</span>
          )}
        </span>
        <ChevronDown
          size={14}
          className={`shrink-0 text-[var(--color-text-muted)] transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute z-[200] left-0 mt-1 min-w-[200px] rounded-md border border-[var(--color-border-muted)] bg-[var(--color-bg-surface)] shadow-xl py-1">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-xs text-[var(--color-text-muted)]">Brak rejonów</div>
          ) : (
            options.map((opt) => (
              <label
                key={opt}
                className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer select-none hover:bg-[var(--color-bg-elevated)]"
              >
                <input
                  type="checkbox"
                  checked={excluded.includes(opt)}
                  onChange={() => onToggle(opt)}
                  className="accent-[var(--color-primary)]"
                />
                <span className="truncate text-[var(--color-text-primary)]">RE {opt}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function ZETendersPortfolioPage() {
  const { data, isLoading } = useZETenders();
  const { data: actionCounts } = useZETenderActionCounts();
  // Zmiany statusów „na żywo" — subskrypcja żyje tylko póki portfel jest otwarty.
  useTenderListLive();
  const user = useAuthStore((s) => s.user);
  const canApprove = !!user?.has_approve_pricing_permission;
  const canIssueOffers = !!user?.has_issue_offers_permission;
  const canCalculate = !!user?.has_calculate_pricing_permission;
  // Kwoty/tura/data złożenia są wrażliwe finansowo → tylko dla osób w obiegu wyceny.
  // Odpowiada gatingowi w ZETenderSerializer.get_offer (backend i tak nie odda `offer`).
  const canSeeAmounts = canApprove || canIssueOffers || canCalculate;
  const [dialog, setDialog] = useState(false);
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [client, setClient] = useState("");
  const [operator, setOperator] = useState("");
  const [network, setNetwork] = useState("");
  const [scope, setScope] = useState("");
  const [status, setStatus] = useState("");
  const [submitted, setSubmitted] = useState(""); // "" | "yes" | "no"
  // Zakres terminu oferty ("YYYY-MM-DD", pusty = bez ograniczenia) i długości trasy w metrach.
  const [deadlineFrom, setDeadlineFrom] = useState("");
  const [deadlineTo, setDeadlineTo] = useState("");
  const [lengthFrom, setLengthFrom] = useState("");
  const [lengthTo, setLengthTo] = useState("");
  const [onlyActive, setOnlyActive] = useState(true); // domyślnie: tylko przed terminem
  // Rejony (RE) wykluczane z listy — wszystkie z EXCLUDABLE_REJONY domyślnie zaznaczone.
  const [excludedRejony, setExcludedRejony] = useState<string[]>(EXCLUDABLE_REJONY);
  const [actionFilter, setActionFilter] = useState<NonNullable<TenderActionStatus> | null>(null);
  // „Powtórki anulowanych" — przetargi, które system rozpoznał jako ponowne ogłoszenie
  // wcześniej anulowanego postępowania. Filtr; samo podbicie na górę listy działa zawsze.
  const [onlyRepeats, setOnlyRepeats] = useState(false);
  const [sort, setSort] = useState<SortState>({ key: "deadline", dir: "asc" });
  const [page, setPage] = useState(1);

  const onSort = (key: SortKey) =>
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
    );

  // Distinct option lists, derived from the loaded rows.
  const distinct = (pick: (t: ZETender) => string | null) =>
    Array.from(
      new Set((data ?? []).map(pick).filter((v): v is string => !!v)),
    ).sort((a, b) => a.localeCompare(b, "pl"));

  const clientOptions = useMemo(() => distinct((t) => t.client_name), [data]);
  const operatorOptions = useMemo(() => distinct((t) => t.operator_name), [data]);
  const networkOptions = useMemo(() => distinct((t) => t.network_type), [data]);
  // Stały zestaw + kubełek „nieustalony", żeby przetargi bez rozpoznanego prefiksu
  // dało się wyfiltrować i uzupełnić — samo `distinct` po prostu by je gubiło.
  const scopeOptions = useMemo(() => {
    const present = new Set((data ?? []).map(effectiveScope));
    const known = Object.keys(SCOPE_LABEL).filter((s) => present.has(s));
    return present.has("") ? [...known, "__none__"] : known;
  }, [data]);
  const statusOptions = STATUS_FILTER_ORDER;

  const now = new Date();

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Granice terminu liczone lokalnie: "od" obejmuje cały dzień od północy, "do" —
    // do końca wybranego dnia, żeby przetarg z terminem 15.09 12:00 wpadł w 15.09–15.09.
    const from = deadlineFrom ? new Date(`${deadlineFrom}T00:00:00`) : null;
    const to = deadlineTo ? new Date(`${deadlineTo}T23:59:59.999`) : null;
    const num = (v: string) => (v.trim() !== "" && Number.isFinite(Number(v)) ? Number(v) : null);
    const minLen = num(lengthFrom);
    const maxLen = num(lengthTo);
    return (data ?? []).filter((t) => {
      if (q) {
        const hay = [t.title, t.reference_number, t.operator_name, t.client_name]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (client && t.client_name !== client) return false;
      if (operator && (t.operator_name || "") !== operator) return false;
      if (network && (t.network_type || "") !== network) return false;
      if (scope && (scope === "__none__" ? effectiveScope(t) !== "" : effectiveScope(t) !== scope))
        return false;
      if (status && t.status !== status) return false;
      if (submitted === "yes" && !t.bid_submitted) return false;
      if (submitted === "no" && t.bid_submitted) return false;
      if (actionFilter && t.action_status !== actionFilter) return false;
      if (onlyRepeats && !t.repeat_match) return false;
      if (onlyActive && t.offer_deadline && new Date(t.offer_deadline) < now) return false;
      // Przedziały: rekord bez terminu / bez znanej długości wypada, gdy pytamy o zakres —
      // nie da się orzec, że mieści się w widełkach.
      if (from || to) {
        if (!t.offer_deadline) return false;
        const d = new Date(t.offer_deadline);
        if (from && d < from) return false;
        if (to && d > to) return false;
      }
      if (minLen !== null || maxLen !== null) {
        const len = effectiveLengthM(t);
        if (len === null) return false;
        if (minLen !== null && len < minLen) return false;
        if (maxLen !== null && len > maxLen) return false;
      }
      if (isRejonExcluded(t.energa_rejon, excludedRejony)) return false;
      return true;
    });
  }, [data, search, client, operator, network, scope, status, submitted, onlyActive, actionFilter, onlyRepeats, excludedRejony, deadlineFrom, deadlineTo, lengthFrom, lengthTo]);

  const sortValue = (t: ZETender, key: SortKey): string | number | null => {
    switch (key) {
      case "title": return t.title?.toLowerCase() ?? "";
      case "client": return t.client_name?.toLowerCase() ?? "";
      case "operator": return t.operator_name?.toLowerCase() ?? "";
      case "network": return t.network_type?.toLowerCase() ?? "";
      case "scope": return scopeLabel(effectiveScope(t) || null).toLowerCase();
      case "length": return effectiveLengthM(t);
      case "deadline": return t.offer_deadline ?? "";
      case "status": return t.status_display || STATUS_LABEL[t.status] || t.status;
      case "submitted": return t.bid_submitted ? 0 : 1;
      case "submittedAt": return t.offer?.submitted_at ?? "";
      case "variants": return t.variants_count;
    }
  };

  const sorted = useMemo(() => {
    // Powtórki anulowanych przetargów idą na górę NIEZALEŻNIE od sortowania — to jest
    // sedno funkcji: taki przetarg ma być przejrzany w pierwszej kolejności, bo trasę i
    // wycenę zwykle da się zaciągnąć zamiast rysować od zera. W obrębie grupy
    // obowiązuje normalne sortowanie kolumnami.
    const bump = (t: ZETender) => (t.repeat_match ? 0 : 1);
    if (!sort.key) return [...rows].sort((a, b) => bump(a) - bump(b));
    const key = sort.key;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const byBump = bump(a) - bump(b);
      if (byBump !== 0) return byBump;
      const va = sortValue(a, key);
      const vb = sortValue(b, key);
      // Missing values (null / empty string) always sort to the bottom.
      const aEmpty = va === null || va === "";
      const bEmpty = vb === null || vb === "";
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb), "pl") * dir;
    });
  }, [rows, sort]);

  // Reset to the first page whenever filters or sorting change, so the visible page
  // never points past the (possibly shorter) new result set.
  useEffect(() => {
    setPage(1);
  }, [search, client, operator, network, scope, status, submitted, onlyActive, actionFilter, onlyRepeats, excludedRejony, deadlineFrom, deadlineTo, lengthFrom, lengthTo, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const total = (data ?? []).length;
  // Domyślne wykluczenie (cała lista RE) nie liczy się jako aktywny filtr — dopiero zmiana zestawu.
  const isDefaultExclusion =
    excludedRejony.length === EXCLUDABLE_REJONY.length &&
    EXCLUDABLE_REJONY.every((x) => excludedRejony.includes(x));
  const hasFilters = !!(search || client || operator || network || scope || status || submitted || !onlyActive || actionFilter || onlyRepeats || !isDefaultExclusion || deadlineFrom || deadlineTo || lengthFrom || lengthTo);
  const clearAll = () => {
    setSearch("");
    setClient("");
    setOperator("");
    setNetwork("");
    setScope("");
    setStatus("");
    setSubmitted("");
    setOnlyActive(true);
    setActionFilter(null);
    setOnlyRepeats(false);
    setExcludedRejony(EXCLUDABLE_REJONY);
    setDeadlineFrom("");
    setDeadlineTo("");
    setLengthFrom("");
    setLengthTo("");
  };

  // „Powtórki anulowanych" — liczone z załadowanej listy (to samo źródło co wiersze),
  // więc licznik zawsze zgadza się z tym, co filtr pokazuje.
  const repeatCount = (data ?? []).filter((t) => t.repeat_match).length;
  const selectRepeats = () => {
    const next = !onlyRepeats;
    setOnlyRepeats(next);
    setActionFilter(null);
    // Powtórka bywa ogłaszana długo po terminie tej anulowanej — nie chowaj ich za
    // domyślnym „tylko przed terminem".
    if (next) setOnlyActive(false);
  };

  // Action-status badge chips — analogous to the main Przetargi page. Counts come from
  // the backend (open ZE tenders) and use the same computation as the per-row
  // action_status, so each chip count matches the list it filters to.
  const actionChips: Array<{ dot: string; count: number; label: string; filter: NonNullable<TenderActionStatus> }> = [];
  if (canApprove)
    actionChips.push({ dot: "bg-amber-400", count: actionCounts?.needs_approval ?? 0, label: "oczekuje na zatwierdzenie wyceny", filter: "needs_approval" });
  if (canIssueOffers)
    actionChips.push({ dot: "bg-red-500", count: actionCounts?.needs_offer ?? 0, label: "wymaga wystawienia oferty", filter: "needs_offer" });
  if (canCalculate) {
    actionChips.push({ dot: "bg-[var(--color-primary)]", count: actionCounts?.needs_pricing ?? 0, label: "wymaga wyceny", filter: "needs_pricing" });
    actionChips.push({ dot: "bg-violet-500", count: actionCounts?.pricing_claimed ?? 0, label: "przejęte — ktoś liczy wycenę", filter: "pricing_claimed" });
  }
  if (canIssueOffers)
    actionChips.push({ dot: "bg-rose-500", count: actionCounts?.needs_result ?? 0, label: "oferta złożona — minęło 21 dni bez wyniku", filter: "needs_result" });

  const selectActionChip = (f: NonNullable<TenderActionStatus>) => {
    const next = actionFilter === f ? null : f;
    setActionFilter(next);
    // Entering a pre-deadline chip switches on "tylko przed terminem" (so the list
    // matches the before-deadline counter); needs_result switches it off; leaving any
    // chip clears it.
    setOnlyActive(next ? PRE_DEADLINE_CHIPS.has(next) : false);
  };

  // Soft-win ("Wygrany - nieoficjalnie") status chip — a status filter, not an
  // action one. Count from the full loaded set (same source as the row list).
  const wonUnofficialCount = (data ?? []).filter(
    (t) => t.status === "won_unofficial",
  ).length;
  const selectWonUnofficial = () => {
    const active = status === "won_unofficial";
    setStatus(active ? "" : "won_unofficial");
    setActionFilter(null);
    // Soft wins usually have a past offer deadline → turn off "tylko przed terminem"
    // so they actually surface in the list.
    setOnlyActive(false);
  };

  return (
    <div className="p-4 md:p-6">
      <PageHeader
        title="Przetargi ZE"
        subtitle="Etap przetargowy — rysowanie tras i wycena ilości · DLC_01"
        actions={<Button size="sm" onClick={() => setDialog(true)}>+ Nowy przetarg</Button>}
      />

      {(actionChips.length > 0 || wonUnofficialCount > 0 || repeatCount > 0) && (
        <div className="flex flex-wrap gap-2 mb-4">
          {repeatCount > 0 && (
            <button
              type="button"
              onClick={selectRepeats}
              title="Przetargi rozpoznane jako ponowne ogłoszenie anulowanego postępowania — trasę i wycenę można zaciągnąć z tamtego przetargu."
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors ${
                onlyRepeats
                  ? "border-[var(--color-primary)] bg-[var(--color-bg-elevated)]"
                  : "border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)]"
              }`}
            >
              <RotateCcw size={14} className="text-amber-600" />
              <span className="font-semibold text-[var(--color-text-primary)]">{repeatCount}</span>
              <span className="text-[var(--color-text-secondary)]">powtórki anulowanych</span>
            </button>
          )}
          {actionChips.map((c) => {
            const isActive = actionFilter === c.filter;
            return (
              <button
                key={c.filter}
                type="button"
                onClick={() => selectActionChip(c.filter)}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors ${
                  isActive
                    ? "border-[var(--color-primary)] bg-[var(--color-bg-elevated)]"
                    : "border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)]"
                }`}
              >
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${c.dot}`} />
                <span className="font-semibold text-[var(--color-text-primary)]">{c.count}</span>
                <span className="text-[var(--color-text-secondary)]">{c.label}</span>
              </button>
            );
          })}
          {wonUnofficialCount > 0 && (
            <button
              type="button"
              onClick={selectWonUnofficial}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors ${
                status === "won_unofficial"
                  ? "border-[var(--color-primary)] bg-[var(--color-bg-elevated)]"
                  : "border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)]"
              }`}
            >
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-400" />
              <span className="font-semibold text-[var(--color-text-primary)]">{wonUnofficialCount}</span>
              <span className="text-[var(--color-text-secondary)]">wygrane nieoficjalnie</span>
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input
          placeholder="Szukaj: tytuł, nr referencyjny, operator, klient…"
          className="input-base w-full sm:w-auto sm:flex-1 sm:min-w-[220px] sm:max-w-md"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FilterSelect
          value={client}
          onChange={setClient}
          options={[
            { value: "", label: "Klient: wszyscy" },
            ...clientOptions.map((c) => ({ value: c, label: c })),
          ]}
        />
        <FilterSelect
          value={operator}
          onChange={setOperator}
          options={[
            { value: "", label: "Operator: wszyscy" },
            ...operatorOptions.map((o) => ({ value: o, label: o })),
          ]}
        />
        <FilterSelect
          value={network}
          onChange={setNetwork}
          options={[
            { value: "", label: "Typ sieci: wszystkie" },
            ...networkOptions.map((n) => ({ value: n, label: n })),
          ]}
        />
        <FilterSelect
          value={scope}
          onChange={setScope}
          options={[
            { value: "", label: "Zakres: wszystkie" },
            ...scopeOptions.map((s) => ({
              value: s,
              label: s === "__none__" ? "Zakres: nieustalony" : scopeLabel(s),
            })),
          ]}
        />
        <FilterSelect
          value={status}
          onChange={setStatus}
          options={[
            { value: "", label: "Status: wszystkie" },
            ...statusOptions.map((s) => ({ value: s, label: STATUS_LABEL[s] ?? s })),
          ]}
        />
        <FilterSelect
          value={submitted}
          onChange={setSubmitted}
          options={[
            { value: "", label: "Oferta: wszystkie" },
            { value: "yes", label: "Złożona" },
            { value: "no", label: "Niezłożona" },
          ]}
        />
        <RangeFilter
          label="Termin"
          type="date"
          from={deadlineFrom}
          to={deadlineTo}
          onFrom={setDeadlineFrom}
          onTo={setDeadlineTo}
          fromTitle="Termin oferty od"
          toTitle="Termin oferty do"
        />
        <RangeFilter
          label="Długość (m)"
          type="number"
          from={lengthFrom}
          to={lengthTo}
          onFrom={setLengthFrom}
          onTo={setLengthTo}
          fromTitle="Długość trasy od (m)"
          toTitle="Długość trasy do (m)"
        />
        <label className="inline-flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)] cursor-pointer select-none whitespace-nowrap">
          <input
            type="checkbox"
            checked={onlyActive}
            onChange={(e) => setOnlyActive(e.target.checked)}
            className="accent-[var(--color-primary)]"
          />
          Tylko przed terminem
        </label>
        <ExcludedRejonyFilter
          options={EXCLUDABLE_REJONY}
          excluded={excludedRejony}
          onToggle={(v) =>
            setExcludedRejony((prev) =>
              prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
            )
          }
        />
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearAll}>
            Wyczyść filtry
          </Button>
        )}
        <span className="section-label ml-auto whitespace-nowrap">
          {rows.length} / {total}
        </span>
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : (
        <TableShell tableClassName="table-fixed min-w-[1280px]">
          <thead>
            <tr>
              <SortHeader label="Tytuł" col="title" sort={sort} onSort={onSort} className="w-[16%]" />
              <SortHeader label="Klient" col="client" sort={sort} onSort={onSort} className="w-[12%]" />
              <SortHeader label="Operator" col="operator" sort={sort} onSort={onSort} className="w-[10%]" />
              <SortHeader label="Typ sieci" col="network" sort={sort} onSort={onSort} className="w-[8%]" />
              <SortHeader label="Zakres" col="scope" sort={sort} onSort={onSort} className="w-[9%]" />
              <SortHeader label="Długość" col="length" sort={sort} onSort={onSort} className="w-[7%] text-right" />
              <SortHeader label="Termin oferty" col="deadline" sort={sort} onSort={onSort} className="w-[9%]" />
              <SortHeader label="Status" col="status" sort={sort} onSort={onSort} className="w-[11%]" />
              <SortHeader label="Oferta" col="submitted" sort={sort} onSort={onSort} className="w-[9%]" />
              {canSeeAmounts && (
                <SortHeader label="Data złożenia" col="submittedAt" sort={sort} onSort={onSort} className="w-[8%]" />
              )}
              <SortHeader label="Warianty" col="variants" sort={sort} onSort={onSort} className="w-[6%] text-right" />
              <Th className="w-[9%] text-center">Do zrobienia</Th>
            </tr>
          </thead>
          <tbody>
            {paged.map((t) => {
              const deadline = t.offer_deadline ? new Date(t.offer_deadline) : null;
              const isPast = deadline ? deadline < now : false;
              const isUrgent = deadline && !isPast
                ? (deadline.getTime() - now.getTime()) < 2 * 24 * 60 * 60 * 1000
                : false;
              return (
              <tr
                key={t.id}
                className={`border-t border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)] transition-colors [&>td]:align-top${isPast ? " opacity-40" : ""}`}
              >
                <td className="px-3 py-2.5">
                  <Link
                    className="block break-words font-medium text-[var(--color-text-primary)] hover:text-[var(--color-primary)] transition-colors"
                    to={`/ze-tenders/${t.id}`}
                    title={t.title}
                  >
                    {t.title}
                  </Link>
                  {t.repeat_match && (
                    <span
                      className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-400"
                      title={
                        `Powtórka anulowanego przetargu: „${t.repeat_match.previous_title}" ` +
                        `(zgodność ${Math.round(t.repeat_match.score * 100)}%` +
                        (t.repeat_match.shared_parcels
                          ? `, wspólne działki: ${t.repeat_match.shared_parcels}`
                          : "") +
                        `). ${t.repeat_match.imported ? "Dane już zaciągnięte." : "Trasę i wycenę można zaciągnąć."}`
                      }
                    >
                      <RotateCcw size={10} />
                      {t.repeat_match.imported ? "Powtórka — zaciągnięta" : "Powtórka anulowanego"}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 break-words text-[var(--color-text-secondary)]">{t.client_name}</td>
                <td className="px-3 py-2.5 break-words text-[var(--color-text-secondary)]">{t.operator_name || "—"}</td>
                <td className="px-3 py-2.5 font-mono break-words text-[var(--color-text-secondary)]">
                  {t.network_type || "—"}
                </td>
                <td className="px-3 py-2.5 break-words text-[var(--color-text-secondary)]">
                  {scopeLabel(effectiveScope(t) || null)}
                  {scopeConflict(t) && (
                    <span
                      className="ml-1 cursor-help font-semibold text-amber-600"
                      title={`Prefiks nazwy mówi „${scopeLabel(t.work_scope)}", analiza AI „${scopeLabel(t.contract_scope)}" — sprawdź.`}
                    >
                      ⚠
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 font-mono break-words text-[var(--color-text-secondary)] text-right">
                  {t.cable_length_m != null
                    ? `${t.cable_length_m.toLocaleString("pl-PL")} m`
                    : t.project_type === "short_connection"
                      ? <span className="text-[var(--color-text-tertiary)] font-sans text-xs">przyłącze</span>
                      : "—"}
                </td>
                <td className={`px-3 py-2.5 font-mono break-words${isUrgent ? " text-red-600 font-semibold" : isPast ? " text-[var(--color-text-muted)] line-through" : " text-[var(--color-text-secondary)]"}`}>
                  {fmtDateTime(t.offer_deadline)}
                </td>
                <td className="px-3 py-2.5 font-mono break-words text-[var(--color-text-secondary)]">
                  {t.status === "won_unofficial" ? (
                    <div className="flex flex-col gap-0.5">
                      <span className="inline-flex w-fit items-center gap-1 font-sans text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                        {t.status_display || STATUS_LABEL[t.status]}
                      </span>
                      <span className="text-[11px] text-[var(--color-text-muted)]">
                        nieof.: {fmtDate(t.won_unofficial_at)}
                        {" · "}przetarg: {fmtDate(t.last_round_deadline ?? t.offer_deadline)}
                      </span>
                    </div>
                  ) : (
                    t.status_display || STATUS_LABEL[t.status] || t.status
                  )}
                </td>
                <td className="px-3 py-2.5">
                  {t.bid_submitted ? (
                    <div className="flex flex-col items-start gap-0.5">
                      <span className="inline-flex items-center gap-1 font-mono text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">✓ Złożona</span>
                      {t.offer && (
                        <span className="font-mono text-[11px] text-[var(--color-text-secondary)]">
                          {fmtPLN(t.offer.value)} · tura {t.offer.round}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="font-mono text-[11px] text-[var(--color-text-muted)]">—</span>
                  )}
                </td>
                {canSeeAmounts && (
                  <td className="px-3 py-2.5 font-mono break-words text-xs text-[var(--color-text-secondary)]">
                    {t.offer
                      ? fmtDate(t.offer.submitted_at)
                      : <span className="text-[var(--color-text-muted)]">—</span>}
                  </td>
                )}
                <td className="px-3 py-2.5 font-mono text-[var(--color-text-secondary)] text-right">{t.variants_count}</td>
                <td className="px-3 py-2.5 text-center">
                  {t.action_status ? (
                    <span className={`inline-flex items-center justify-center text-center break-words px-2 py-0.5 rounded text-[11px] font-medium ${ACTION_STATUS_COLORS[t.action_status]}`}>
                      {tenderActionLabel(t.action_status, t.pricing_claimed_by_name ?? t.pending_pricing_initials)}
                    </span>
                  ) : (
                    <span className="text-[var(--color-text-muted)] text-xs">—</span>
                  )}
                </td>
              </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={canSeeAmounts ? 12 : 11} className="px-3 py-10 text-center section-label tracking-wide">
                  {total === 0 ? "Brak przetargów ZE." : "Brak wyników dla wybranych filtrów."}
                </td>
              </tr>
            )}
          </tbody>
        </TableShell>
      )}

      {!isLoading && totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 px-1">
          <span className="section-label">
            Strona {safePage} z {totalPages} ({sorted.length} przetargów)
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="p-1.5 rounded border border-[var(--color-border-muted)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
              .reduce<Array<number | "ellipsis">>((acc, p, idx, arr) => {
                if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("ellipsis");
                acc.push(p);
                return acc;
              }, [])
              .map((item, idx) =>
                item === "ellipsis" ? (
                  <span key={`e${idx}`} className="px-1.5 text-xs text-[var(--color-text-muted)]">…</span>
                ) : (
                  <button
                    key={item}
                    onClick={() => setPage(item)}
                    className={`min-w-[28px] px-2 py-1 rounded border text-xs transition-colors ${
                      item === safePage
                        ? "bg-[var(--color-primary)] border-[var(--color-primary)] text-white"
                        : "border-[var(--color-border-muted)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
                    }`}
                  >
                    {item}
                  </button>
                ),
              )}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="p-1.5 rounded border border-[var(--color-border-muted)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {dialog && (
        <ZETenderCreateDialog onClose={() => setDialog(false)} onCreated={(id) => navigate(`/ze-tenders/${id}`)} />
      )}
    </div>
  );
}
